import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PageWrapper from './PageWrapper';
import EditableText from './EditableText';
import { selectPageSlice } from '../Selector';

// Must match index.scss variables
const PAGE_HEIGHT = 1123;
const PADDING_TOP = 50;
const PADDING_BOTTOM = 60;
const FOOTER_HEIGHT = 40;
const CONTENT_HEIGHT = PAGE_HEIGHT - PADDING_TOP - PADDING_BOTTOM - FOOTER_HEIGHT; // 973px
const CONTENT_WIDTH = 794 - 60 - 60; // 674px

const DATA_COLUMNS = [
  { key: 'self', label: 'SELF' },
  { key: 'allButSelfAverageScore', label: 'ALL BUT SELF' },
  { key: 'manager', label: 'MANAGER' },
  { key: 'teamMemberAverageScore', label: 'TEAM MEMBER' },
  { key: 'teamMemberConsensus', label: 'CONSENSUS' },
  { key: 'peersAverageScore', label: 'PEER' },
  { key: 'peersConsensus', label: 'CONSENSUS' },
];

function DimensionHeading({ dimension }) {
  return (
    <EditableText
      as="h2"
      className="pdf-section-heading"
      path={`standardDeviationSection[${dimension}].dimensionLabel`}
      variant="inline"
      stripInlineWrap
      fallback={(dimension || '').toUpperCase()}
    />
  );
}

function TableHead() {
  return (
    <thead>
      <tr>
        <th className="col-capability">COMPETENCY</th>
        <th className="col-behaviors">BEHAVIORS</th>
        {DATA_COLUMNS.map((col) => (
          <th key={col.key} className="col-data-header">
            <span>{col.label}</span>
          </th>
        ))}
      </tr>
    </thead>
  );
}

function TableRow({ row, subCat, subRows, idx }) {
  return (
    <tr>
      {idx === 0 && (
        <td rowSpan={subRows.length} className="col-capability">
          {subCat}
        </td>
      )}
      <td className="col-behaviors">{row.behaviour}</td>
      {DATA_COLUMNS.map((col) => (
        <td key={col.key} className="col-data">{row[col.key]}</td>
      ))}
    </tr>
  );
}

function TablePage({ dimension, entries, pageNumber, personName }) {
  return (
    <PageWrapper pageNumber={pageNumber} personName={personName}>
      <DimensionHeading dimension={dimension} />
      <div className="std-table-wrapper">
        <table className="pdf-table pdf-table--appendix">
          <TableHead />
          <tbody>
            {entries.map(([subCat, subRows]) =>
              subRows.map((row, idx) => (
                <TableRow
                  key={`${subCat}-${idx}`}
                  row={row}
                  subCat={subCat}
                  subRows={subRows}
                  idx={idx}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageWrapper>
  );
}

function StandardDeviationPage({ data, personName, pageNumber, slice }) {
  const { dimension, tableContents } = data;
  const rows = tableContents.contents;

  const isFirstSliceRef = useRef(true);

  // Group rows by sub-category (capability)
  const groupedBySubCategory = {};
  rows.forEach(row => {
    const sub = row.subCategory;
    if (!groupedBySubCategory[sub]) groupedBySubCategory[sub] = [];
    groupedBySubCategory[sub].push(row);
  });
  const entries = Object.entries(groupedBySubCategory);

  const measureRef = useRef(null);
  const [pageEntryGroups, setPageEntryGroups] = useState(null);

  useEffect(() => {
    if (isFirstSliceRef.current) {
      isFirstSliceRef.current = false;
      return;
    }
    setPageEntryGroups(null);
  }, [slice]);

  useLayoutEffect(() => {
    if (pageEntryGroups) return;
    if (!measureRef.current) return;

    const container = measureRef.current;
    const titleEl = container.querySelector('[data-std="title"]');
    const theadEl = container.querySelector('[data-std="thead"]');

    const trEls = Array.from(container.querySelectorAll('tr[data-group]'));
    const groupHeights = {};
    trEls.forEach(tr => {
      const g = parseInt(tr.getAttribute('data-group'), 10);
      groupHeights[g] = (groupHeights[g] || 0) + tr.getBoundingClientRect().height;
    });

    const tableEl = container.querySelector('[data-std="table"]');

    const titleH = titleEl ? titleEl.getBoundingClientRect().height : 0;
    const theadH = theadEl ? theadEl.getBoundingClientRect().height : 0;
    const actualTableH = tableEl ? tableEl.getBoundingClientRect().height : 0;

    const sumOfRowH = Object.values(groupHeights).reduce((s, h) => s + h, 0);
    const tableOverhead = Math.max(0, actualTableH - theadH - sumOfRowH);

    const available = CONTENT_HEIGHT - titleH - theadH - tableOverhead;

    const allPages = [];
    let pageGroup = [];
    let usedH = 0;

    entries.forEach((entry, idx) => {
      const h = groupHeights[idx] || 0;

      if (pageGroup.length > 0 && usedH + h > available) {
        allPages.push(pageGroup);
        pageGroup = [entry];
        usedH = h;
      } else {
        pageGroup.push(entry);
        usedH += h;
      }
    });

    if (pageGroup.length > 0) {
      allPages.push(pageGroup);
    }

    setPageEntryGroups(allPages.length > 0 ? allPages : [entries]);
  }, [pageEntryGroups, entries]);

  // ── Phase 1: measure ───────────────────────────────────────────────────────
  if (!pageEntryGroups) {
    return (
      <div style={{ position: 'relative', height: 0, overflow: 'hidden' }}>
      <div
        ref={measureRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: CONTENT_WIDTH,
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <div data-std="title">
          <DimensionHeading dimension={dimension} />
        </div>
        <div className="std-table-wrapper">
          <table data-std="table" className="pdf-table pdf-table--appendix">
            <thead data-std="thead">
              <tr>
                <th className="col-capability">COMPETENCY</th>
                <th className="col-behaviors">BEHAVIORS</th>
                {DATA_COLUMNS.map((col) => (
                  <th key={col.key} className="col-data-header">
                    <span>{col.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map(([subCat, subRows], groupIdx) =>
                subRows.map((row, idx) => (
                  <tr key={`${subCat}-${idx}`} data-group={groupIdx}>
                    {idx === 0 && (
                      <td rowSpan={subRows.length} className="col-capability">
                        {subCat}
                      </td>
                    )}
                    <td className="col-behaviors">{row.behaviour}</td>
                    {DATA_COLUMNS.map((col) => (
                      <td key={col.key} className="col-data">{row[col.key]}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    );
  }

  // ── Phase 2: render ────────────────────────────────────────────────────────
  return (
    <>
      {pageEntryGroups.map((pageEntries, idx) => (
        <TablePage
          key={idx}
          dimension={dimension}
          entries={pageEntries}
          pageNumber={pageNumber + idx}
          personName={personName}
        />
      ))}
    </>
  );
}

const mapStateToProps = (state, ownProps) => ({
  slice: selectPageSlice(
    state,
    'standardDeviationSection',
    ownProps.data && ownProps.data.dimension
  ),
});

export default connect(mapStateToProps)(StandardDeviationPage);
